import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, ExternalLink, Radio } from "lucide-react";
import {
  channels as channelsApi,
  type Channel,
  type ChannelInput,
} from "@/api/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InlineEdit } from "@/components/InlineEdit";
import { EmptyState } from "@/components/EmptyState";
import { PageHeading } from "@/components/PageHeading";
import { useAuth } from "@/lib/AuthContext";
import { can } from "@/lib/permissions";
import { cn } from "@/lib/utils";

export function ChannelsPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const canEdit = can(user, "edit_operational");

  const channelsQ = useQuery({ queryKey: ["channels"], queryFn: () => channelsApi.list() });
  const channels = channelsQ.data?.channels ?? [];

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["channels"] });

  const createMut = useMutation({
    mutationFn: (name: string) => channelsApi.create({ name }),
    onSettled: invalidate,
  });
  const updateMut = useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: ChannelInput }) =>
      channelsApi.update(id, patch),
    onSettled: invalidate,
  });
  const deleteMut = useMutation({
    mutationFn: (id: number) => channelsApi.remove(id),
    onSettled: invalidate,
  });

  const [newName, setNewName] = useState("");
  function addChannel() {
    const name = newName.trim();
    if (name === "" || createMut.isPending) return;
    createMut.mutate(name, { onSuccess: () => setNewName("") });
  }

  return (
    <div className="max-w-2xl space-y-5">
      <PageHeading
        title="Integrations"
        sub="The surfaces you publish to. Each gets a color so it stands out on the board."
        eyebrow="Kovach Consulting · Integrations"
      />

      {canEdit ? (
        <Card>
          <CardContent className="flex items-center gap-2 p-3">
            <Plus size={16} className="shrink-0 text-muted-foreground" aria-hidden />
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addChannel();
                }
              }}
              placeholder="Add an integration — e.g. Threads, TikTok, a newsletter…"
              className="h-9 border-0 bg-transparent px-0 shadow-none focus-visible:ring-0"
            />
            <Button
              size="sm"
              onClick={addChannel}
              disabled={newName.trim() === "" || createMut.isPending}
            >
              {createMut.isPending ? "Adding…" : "Add"}
            </Button>
          </CardContent>
        </Card>
      ) : null}

      {channelsQ.isLoading ? (
        <div className="py-16 text-center text-sm text-muted-foreground">Loading…</div>
      ) : channels.length === 0 ? (
        <EmptyState
          icon={Radio}
          label="No integrations"
          hint={
            canEdit
              ? "Add the first surface you publish to using the field above."
              : "No integrations have been set up yet."
          }
        />
      ) : (
        <Card>
          <CardContent className="p-0">
            {channels.map((ch) => (
              <ChannelRow
                key={ch.id}
                channel={ch}
                canEdit={canEdit}
                onUpdate={(patch) => updateMut.mutateAsync({ id: ch.id, patch })}
                onDelete={() => deleteMut.mutateAsync(ch.id)}
              />
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function ChannelRow({
  channel,
  canEdit,
  onUpdate,
  onDelete,
}: {
  channel: Channel;
  canEdit: boolean;
  onUpdate: (patch: ChannelInput) => Promise<unknown>;
  onDelete: () => Promise<unknown>;
}) {
  const active = channel.is_active !== 0;

  return (
    <div className="flex flex-col gap-2 border-b px-3 py-3 last:border-b-0 sm:flex-row sm:items-center">
      {/* Color swatch */}
      <label className="relative inline-grid size-6 shrink-0 place-items-center">
        <span
          className="size-3.5 rounded-full ring-1 ring-border"
          style={{ backgroundColor: channel.color ?? "#cbd5e1" }}
          aria-hidden
        />
        {canEdit ? (
          <input
            type="color"
            value={channel.color ?? "#888888"}
            onChange={(e) => void onUpdate({ color: e.target.value })}
            className="absolute inset-0 cursor-pointer opacity-0"
            aria-label={`${channel.name} color`}
          />
        ) : null}
      </label>

      {/* Name + handle + url */}
      <div className="min-w-0 flex-1">
        {canEdit ? (
          <InlineEdit
            value={channel.name}
            required
            onSave={(next) => onUpdate({ name: next })}
            className="font-medium"
            inputClassName="font-medium"
          />
        ) : (
          <div className="px-3 py-2 text-sm font-medium">{channel.name}</div>
        )}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pl-3 pt-0.5 text-xs text-muted-foreground">
          {canEdit ? (
            <InlineEdit
              value={channel.handle}
              placeholder="@handle"
              onSave={(next) => onUpdate({ handle: next })}
              className="px-0 py-0 text-xs"
            />
          ) : channel.handle ? (
            <span>{channel.handle}</span>
          ) : null}
          {channel.url ? (
            <a
              href={channel.url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 hover:text-foreground"
            >
              <ExternalLink size={11} aria-hidden />
              Visit
            </a>
          ) : null}
        </div>
      </div>

      {/* Active toggle + delete */}
      <div className="flex shrink-0 items-center gap-2 pl-3 sm:pl-0">
        <button
          type="button"
          disabled={!canEdit}
          onClick={() => void onUpdate({ is_active: active ? 0 : 1 })}
          className={cn(
            "rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
            active
              ? "border-success/60 bg-success/10 text-foreground"
              : "border-border bg-muted text-muted-foreground",
            !canEdit && "pointer-events-none opacity-80",
          )}
        >
          {active ? "Active" : "Hidden"}
        </button>
        {canEdit ? (
          <button
            type="button"
            aria-label="Delete integration"
            onClick={() => {
              if (confirm(`Delete "${channel.name}"? Its content stays, just unassigned.`)) {
                void onDelete();
              }
            }}
            className="grid size-7 place-items-center rounded-md text-muted-foreground/60 transition-colors hover:bg-muted hover:text-destructive"
          >
            <Trash2 size={14} />
          </button>
        ) : null}
      </div>
    </div>
  );
}
